import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { User, Menu, X, ChevronDown, Settings, Activity, Sun, Moon } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';

interface NavbarProps {
  isLoggedIn: boolean;
  isAdmin?: boolean;
  onLogout: () => void | Promise<void>;
}

interface NavItem {
  path: string;
  label: string;
  isPrimary?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ isLoggedIn, isAdmin, onLogout }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const accountDropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { resolved, setTheme } = useTheme();
  const isDark = resolved === 'dark';

  // Labelled with the mode it switches to, so the action is unambiguous.
  const themeToggle = (
    <button
      className="theme-toggle-btn"
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {isDark ? <Sun size={18} /> : <Moon size={18} />}
      <span>{isDark ? 'Light mode' : 'Dark mode'}</span>
    </button>
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(event.target as Node)) {
        setIsAccountOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleLogout = async () => {
    setIsAccountOpen(false);
    setIsMenuOpen(false);
    await onLogout();
    navigate('/');
  };

  const baseNavItems: NavItem[] = [
    { path: '/', label: 'Home' },
    { path: '/privacy-terms', label: 'Privacy & Terms' },
  ];

  const loggedInNavItems: NavItem[] = [
    { path: '/', label: 'Home' },
    { path: '/talkshop', label: 'Talk Shop' },
    { path: '/privacy-terms', label: 'Privacy & Terms' },
    { path: '/pull-library', label: 'Pull Library', isPrimary: true },
  ];

  const navItems = isLoggedIn ? loggedInNavItems : baseNavItems;

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className="navbar">
      <div className="nav-container">
        <Link to="/" className="nav-brand">
          <img
            src="/back40PitstopLogo.png"
            alt="Back40 Pitstop"
            className="nav-logo"
          />
        </Link>

        <div className={`nav-links ${isMenuOpen ? 'nav-links-open' : ''}`}>
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-link ${item.isPrimary ? 'nav-link-primary' : ''} ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => setIsMenuOpen(false)}
            >
              {item.isPrimary && <Activity size={14} />}
              {item.label}
            </Link>
          ))}

          {/* Mobile-only auth buttons inside dropdown */}
          <div className="nav-auth-mobile">
            {themeToggle}
            {isLoggedIn ? (
              <>
                <Link
                  to="/profile"
                  className="nav-link"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <User size={20} />
                  <span>Profile</span>
                </Link>
                <Link
                  to="/settings"
                  className="nav-link"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Settings size={20} />
                  <span>Settings</span>
                </Link>
                <button
                  className="nav-link logout-link"
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <button
                  className="nav-link"
                  onClick={() => {
                    handleLoginClick();
                    setIsMenuOpen(false);
                  }}
                >
                  Login
                </button>
              </>
            )}
          </div>
        </div>

        <div className="nav-auth">
          {themeToggle}
          {isLoggedIn ? (
            <div className="nav-user" ref={accountDropdownRef}>
              <button
                className="account-dropdown-btn"
                onClick={() => setIsAccountOpen(!isAccountOpen)}
                aria-expanded={isAccountOpen}
              >
                <User size={20} />
                <span>Account</span>
                <ChevronDown size={16} className={`dropdown-chevron ${isAccountOpen ? 'open' : ''}`} />
              </button>
              {isAccountOpen && (
                <div className="account-dropdown-menu">
                  <Link
                    to="/profile"
                    className="account-dropdown-item"
                    onClick={() => setIsAccountOpen(false)}
                  >
                    Profile
                  </Link>
                  <Link
                    to="/settings"
                    className="account-dropdown-item"
                    onClick={() => setIsAccountOpen(false)}
                  >
                    Settings
                  </Link>
                  {isAdmin && (
                    <>
                      <div className="account-dropdown-divider" />
                      <Link
                        to="/admin"
                        className="account-dropdown-item account-dropdown-admin"
                        onClick={() => setIsAccountOpen(false)}
                      >
                        Admin
                      </Link>
                    </>
                  )}
                  <div className="account-dropdown-divider" />
                  <button
                    className="account-dropdown-item account-dropdown-logout"
                    onClick={handleLogout}
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="auth-buttons">
              <button
                className="login-btn"
                onClick={handleLoginClick}
              >
                Login
              </button>
            </div>
          )}
        </div>

        <button
          className="mobile-menu-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
    </nav>
  );
};

export default Navbar;